/* eslint-disable no-console */
import type { ThinkingStep } from '@landtrustinc/design-system';
import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  ChatMessage,
  QueueStatus,
  ResponseDirective,
  UsePackageChatOptions,
  UsePackageChatReturn,
  WebSocketMessage,
} from '../types';
import { sendFeedback as sendFeedbackApi } from '../utils/feedbackApi';

// Constants for throttle retry behavior
const MAX_AUTO_RETRIES = 3;
const MIN_RETRY_DELAY = 5; // Minimum seconds before retry

const isQualifyingDirective = (directive: ResponseDirective | null | undefined): boolean =>
  directive === 'ask_qualifying_question';

/**
 * Custom hook for managing package chat with WebSocket connection
 * Connects to a Bedrock Agent via WebSocket and manages message state
 */
const usePackageChat = ({
  listingId,
  packageId,
  pageType,
  currentUser,
  config,
}: UsePackageChatOptions): UsePackageChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInputDisabled, setIsInputDisabled] = useState(false); // Disabled when budget exhausted
  const [currentTraceMessage, setCurrentTraceMessage] = useState(''); // User-facing trace message
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]); // Accumulated thinking steps
  const [activeResponseDirective, setActiveResponseDirective] = useState<ResponseDirective | null>(
    null
  );
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const connectionPromiseRef = useRef<Promise<void> | null>(null);
  const messageBufferRef = useRef<Record<string, string>>({}); // Buffer to accumulate chunks by messageId
  const messageDirectiveRef = useRef<Record<string, ResponseDirective>>({}); // Directive per messageId
  const activeResponseDirectiveRef = useRef<ResponseDirective | null>(null);
  const packageCardsBufferRef = useRef<ChatMessage['packageCards'] | null>(null); // Buffer package_cards until complete
  const conversationIdRef = useRef<string | null>(null); // Server-assigned conversationId for analytics
  const throttleRetryCountRef = useRef(0); // Track auto-retry attempts
  const throttleRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timer for auto-retry
  const lastUserMessageRef = useRef<string | null>(null); // Store last user message for retry
  const cancelledMessageIdsRef = useRef<Set<string>>(new Set()); // Track cancelled message IDs to ignore their responses
  const pendingMessageIdRef = useRef<string | null>(null); // Track the current pending message ID

  const enableDebugLogs = config.enableDebugLogs ?? false;
  const buckChatUrl = config.websocketUrl;
  const buckFeedbackUrl = config.feedbackUrl;
  const buckTokenUrl = config.tokenUrl;

  // Debug: Log WebSocket URL on mount
  useEffect(() => {
    if (enableDebugLogs) {
      console.log('🔧 usePackageChat initialized with config:', config);
    }
  }, [enableDebugLogs, buckChatUrl, buckTokenUrl, listingId, packageId]);

  // Cleanup retry timer on unmount
  useEffect(() => {
    return () => {
      if (throttleRetryTimerRef.current) {
        clearTimeout(throttleRetryTimerRef.current);
      }
    };
  }, []);

  /**
   * Request auth token to be set as HttpOnly cookie
   * The backend sets the token as a secure HttpOnly cookie that is automatically
   * sent with the WebSocket upgrade request. This prevents XSS attacks and token scraping.
   *
   * Local Development:
   * - Use the WebSocket proxy (ws://localhost:8080) to forward cookies
   * - See docker/ws-proxy/README.md for setup instructions
   */
  const requestAuthToken = useCallback(async (): Promise<boolean> => {
    if (!buckTokenUrl) {
      if (enableDebugLogs) {
        console.warn('Token URL not configured. Attempting connection without token.');
      }
      return false;
    }

    try {
      if (enableDebugLogs) {
        console.log('🔑 Requesting auth token (HttpOnly cookie) from:', buckTokenUrl);
      }

      const response = await fetch(buckTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies with request
        // No body needed - backend gets userId from session and anonId from cookies
      });

      if (!response.ok) {
        throw new Error(`Token request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (enableDebugLogs) {
        console.log('✅ Token cookie set, expires at:', data.expiresAt);
      }

      return data.success === true;
    } catch (error) {
      console.error('❌ Failed to request auth token:', error);
      return false;
    }
  }, [buckTokenUrl, enableDebugLogs]);

  /**
   * Check if a message ID has been cancelled and should be ignored
   */
  const isMessageCancelled = useCallback((messageId: string | undefined): boolean => {
    if (!messageId) return false;
    return cancelledMessageIdsRef.current.has(messageId);
  }, []);

  const setActiveDirective = useCallback((directive: ResponseDirective | null) => {
    activeResponseDirectiveRef.current = directive;
    setActiveResponseDirective(directive);
  }, []);

  const resetDirectiveState = useCallback(() => {
    setActiveDirective(null);
  }, [setActiveDirective]);

  const captureResponseDirective = useCallback(
    (directive: ResponseDirective | undefined, messageId?: string) => {
      if (!directive) return;

      setActiveDirective(directive);
      if (messageId) {
        messageDirectiveRef.current[messageId] = directive;
      }

      if (isQualifyingDirective(directive)) {
        setThinkingSteps([]);
        setCurrentTraceMessage('');
      }
    },
    [setActiveDirective]
  );

  const resolveMessageDirective = useCallback(
    (messageId: string, wsDirective?: ResponseDirective): ResponseDirective | undefined =>
      wsDirective ?? messageDirectiveRef.current[messageId],
    []
  );

  // Helper to send message to WebSocket
  const sendToWebSocketInternal = useCallback(
    (messageContent: string) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        // Get user ID from currentUser if available
        const userId = currentUser?.id?.uuid;

        const payload = {
          message: messageContent,
          timestamp: new Date().toISOString(),
          userId: userId || undefined,
        };
        if (enableDebugLogs) console.log('Sending message to WebSocket:', payload);
        wsRef.current.send(JSON.stringify(payload));
        setIsLoading(true);
        resetDirectiveState();
        setThinkingSteps([{ text: 'Understanding your question...', status: 'active' as const }]);
        // Store for potential retry
        lastUserMessageRef.current = messageContent;
        // Generate a pending message ID that we'll match against server responses
        // The server will send back a messageId that starts with "msg-"
        pendingMessageIdRef.current = `pending-${Date.now()}`;
      }
    },
    [enableDebugLogs, currentUser, resetDirectiveState]
  );

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback(
    (wsMessage: WebSocketMessage) => {
      switch (wsMessage.type) {
        case 'connected': {
          // Server sends conversationId when connection is established
          const connectedData = wsMessage.data as { conversationId?: string } | undefined;
          if (connectedData?.conversationId) {
            conversationIdRef.current = connectedData.conversationId;
            if (enableDebugLogs) {
              console.log('🔗 Connected with conversationId:', connectedData.conversationId);
            }
          }
          break;
        }

        case 'processing': {
          // Worker has started processing - update trace message
          if (enableDebugLogs) {
            console.log('⚙️ Processing:', wsMessage);
          }

          // Check if this message was cancelled
          if (isMessageCancelled(wsMessage.messageId)) {
            if (enableDebugLogs) {
              console.log('🚫 Ignoring cancelled message:', wsMessage.messageId);
            }
            return;
          }

          // Track this messageId as the active one
          pendingMessageIdRef.current = wsMessage.messageId ?? null;

          captureResponseDirective(wsMessage.responseDirective, wsMessage.messageId);

          if (!isQualifyingDirective(wsMessage.responseDirective)) {
            setCurrentTraceMessage((wsMessage.data as string) || 'Processing your request...');
            setThinkingSteps([
              { text: 'Understanding your question...', status: 'active' as const },
            ]);
          }
          // Clear any queue status since we're now processing
          setQueueStatus(null);
          break;
        }

        case 'message': {
          // Empty message to establish messageId - just initialize the buffer
          if (wsMessage.messageId) {
            // Check if this message was cancelled
            if (isMessageCancelled(wsMessage.messageId)) {
              if (enableDebugLogs) {
                console.log('🚫 Ignoring cancelled message:', wsMessage.messageId);
              }
              return;
            }
            messageBufferRef.current[wsMessage.messageId] = '';
          }
          break;
        }

        case 'trace': {
          // Log trace messages to console when debug is enabled
          if (enableDebugLogs) {
            console.log('🔍 Trace:', wsMessage);
          }

          captureResponseDirective(wsMessage.responseDirective, wsMessage.messageId);

          if (isQualifyingDirective(activeResponseDirectiveRef.current)) {
            break;
          }

          // Update the user-facing trace message if provided
          if (wsMessage.userMessage) {
            const stepText = wsMessage.userMessage;
            setCurrentTraceMessage(stepText);
            setThinkingSteps((prev) => {
              const lastStep = prev[prev.length - 1];
              if (lastStep?.status === 'active' && lastStep.text === stepText) {
                return prev;
              }

              const completed = prev.map((s) =>
                s.status === 'active' ? { ...s, status: 'completed' as const } : s
              );
              return [...completed, { text: stepText, status: 'active' as const }];
            });
          }
          // Detailed traces are not displayed in the UI, only logged
          break;
        }

        case 'chunk': {
          // Buffer the chunk - don't update state yet
          if (wsMessage.messageId && wsMessage.data) {
            // Check if this message was cancelled
            if (isMessageCancelled(wsMessage.messageId)) {
              // Silently ignore chunks from cancelled messages
              return;
            }

            // Initialize buffer if it doesn't exist
            if (!messageBufferRef.current[wsMessage.messageId]) {
              messageBufferRef.current[wsMessage.messageId] = '';
            }

            // Append chunk to buffer
            messageBufferRef.current[wsMessage.messageId] += wsMessage.data as string;
          }
          break;
        }

        case 'complete': {
          // Check if this message was cancelled
          if (wsMessage.messageId && isMessageCancelled(wsMessage.messageId)) {
            if (enableDebugLogs) {
              console.log('🚫 Ignoring completed cancelled message:', wsMessage.messageId);
            }
            // Clean up the buffers and cancelled set
            delete messageBufferRef.current[wsMessage.messageId];
            packageCardsBufferRef.current = null;
            delete messageDirectiveRef.current[wsMessage.messageId];
            cancelledMessageIdsRef.current.delete(wsMessage.messageId);
            resetDirectiveState();
            return;
          }

          // Now add the complete message to the messages array
          if (wsMessage.messageId) {
            const messageId = wsMessage.messageId;
            const bufferedContent = messageBufferRef.current[messageId] || '';
            const bufferedPackageCards = packageCardsBufferRef.current;
            const directive = resolveMessageDirective(messageId, wsMessage.responseDirective);
            const isQualifying = isQualifyingDirective(directive);

            // Clear the package cards buffer before the state update
            packageCardsBufferRef.current = null;

            setMessages((prev) => [
              ...prev,
              {
                id: messageId,
                role: 'assistant',
                content: bufferedContent,
                showHelpfulQuestion: !isQualifying,
                markdown: true,
                ...(directive ? { responseDirective: directive } : {}),
                ...(bufferedPackageCards ? { packageCards: bufferedPackageCards } : {}),
              },
            ]);

            // Clean up the buffer
            delete messageBufferRef.current[wsMessage.messageId];
            delete messageDirectiveRef.current[messageId];
          }
          // Reset retry count on successful completion
          throttleRetryCountRef.current = 0;
          pendingMessageIdRef.current = null;
          resetDirectiveState();
          setCurrentTraceMessage(''); // Clear trace message on completion
          setThinkingSteps([]);
          setQueueStatus(null); // Clear queue status
          setIsLoading(false);
          break;
        }

        case 'throttled': {
          if (enableDebugLogs) {
            console.log('🚦 Throttled:', wsMessage);
          }

          const retryAfter = Math.max(wsMessage.retryAfter || 10, MIN_RETRY_DELAY);
          const currentRetryCount = throttleRetryCountRef.current;

          // Check if we should auto-retry
          if (currentRetryCount < MAX_AUTO_RETRIES && lastUserMessageRef.current) {
            throttleRetryCountRef.current += 1;

            if (enableDebugLogs) {
              console.log(
                `🔄 Auto-retry ${throttleRetryCountRef.current}/${MAX_AUTO_RETRIES} in ${retryAfter}s`
              );
            }

            // Add throttled message with auto-retry countdown
            const throttledMessageId = wsMessage.messageId || `throttled-${Date.now()}`;
            setMessages((prev) => [
              ...prev,
              {
                id: throttledMessageId,
                role: 'assistant',
                content: '',
                isThrottled: true,
                throttleMessage:
                  (wsMessage.data as string) || 'System is experiencing high demand.',
                retryAfter,
                retryCount: throttleRetryCountRef.current,
                maxRetries: MAX_AUTO_RETRIES,
                isAutoRetrying: true,
                showDisclaimer: false,
                showHelpfulQuestion: false,
              },
            ]);

            // Keep loading state but show throttle message
            // Schedule auto-retry
            throttleRetryTimerRef.current = setTimeout(() => {
              // Remove the throttled message and retry
              setMessages((prev) => prev.filter((msg) => msg.id !== throttledMessageId));
              if (lastUserMessageRef.current) {
                sendToWebSocketInternal(lastUserMessageRef.current);
              }
            }, retryAfter * 1000);
          } else {
            // Max retries exceeded - show error with manual retry option
            throttleRetryCountRef.current = 0; // Reset for next attempt
            setMessages((prev) => [
              ...prev,
              {
                id: wsMessage.messageId || `throttled-${Date.now()}`,
                role: 'assistant',
                content: '',
                isThrottled: true,
                throttleMessage:
                  (wsMessage.data as string) ||
                  "We're experiencing high demand. Please wait a moment and try again.",
                retryAfter,
                retryCount: MAX_AUTO_RETRIES,
                maxRetries: MAX_AUTO_RETRIES,
                isAutoRetrying: false,
                showDisclaimer: false,
                showHelpfulQuestion: false,
              },
            ]);
            setCurrentTraceMessage('');
            setThinkingSteps([]);
            resetDirectiveState();
            setIsLoading(false);
          }
          break;
        }

        case 'backpressure': {
          if (enableDebugLogs) {
            console.log('🔴 Backpressure:', wsMessage);
          }

          const retryAfter = wsMessage.retryAfter || 60;

          // Backpressure = system at capacity, no auto-retry (longer wait expected)
          setMessages((prev) => [
            ...prev,
            {
              id: wsMessage.messageId || `backpressure-${Date.now()}`,
              role: 'assistant',
              content: '',
              isBackpressure: true,
              backpressureMessage:
                (wsMessage.data as string) ||
                `We're experiencing very high demand right now. Please try again in ${retryAfter} seconds.`,
              retryAfter,
              showDisclaimer: false,
              showHelpfulQuestion: false,
            },
          ]);
          setCurrentTraceMessage('');
          setThinkingSteps([]);
          resetDirectiveState();
          setIsLoading(false);
          break;
        }

        case 'queued': {
          if (enableDebugLogs) {
            console.log('📬 Queued:', wsMessage);
          }

          // Check if this message was already cancelled
          if (isMessageCancelled(wsMessage.messageId)) {
            if (enableDebugLogs) {
              console.log('🚫 Ignoring queued status for cancelled message:', wsMessage.messageId);
            }
            return;
          }

          // Track this messageId as the active one (for cancellation)
          pendingMessageIdRef.current = wsMessage.messageId ?? null;

          // Use the message from the backend
          const queueMessage =
            (wsMessage.data as string) ||
            `Your request is queued. Position: ${wsMessage.queuePosition || '?'}`;

          // Set queue status for display
          setQueueStatus({
            position: wsMessage.queuePosition,
            estimatedWait: wsMessage.estimatedWaitTime || 30,
            message: queueMessage,
            messageId: wsMessage.messageId,
          });

          // Update trace message with backend-provided message
          setCurrentTraceMessage(queueMessage);

          // Keep isLoading = true - response will come eventually
          break;
        }

        case 'error': {
          console.error('Bedrock agent error:', wsMessage.data);
          // Reset retry count on error
          throttleRetryCountRef.current = 0;
          // Add error message
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              role: 'assistant',
              content: '',
              hasError: true,
              errorMessage:
                'There was an error. Try again in a couple minutes, or contact support at 406-709-8450',
              showDisclaimer: false,
              showHelpfulQuestion: false,
            },
          ]);
          setCurrentTraceMessage('');
          setThinkingSteps([]);
          resetDirectiveState();
          setQueueStatus(null);
          setIsLoading(false);
          break;
        }

        case 'upgrade_prompt': {
          if (enableDebugLogs) {
            console.log('🚀 Upgrade prompt received:', wsMessage);
          }
          // Add upgrade prompt message with CTA and context from WebSocket
          setMessages((prev) => [
            ...prev,
            {
              id: wsMessage.messageId || Date.now().toString(),
              role: 'assistant',
              content: 'Create an account to gain more access.',
              isUpgradePrompt: true,
              ctaLabel: 'Sign Up',
              ctaUrl: '/signup',
              otpContext: wsMessage.context, // Capture context from backend
              showDisclaimer: false,
              showHelpfulQuestion: false,
            },
          ]);
          setCurrentTraceMessage('');
          setThinkingSteps([]);
          resetDirectiveState();
          setIsLoading(false);
          break;
        }

        case 'signup_required':
        case 'signup_cta': {
          if (enableDebugLogs) {
            console.log('📝 Signup CTA received:', wsMessage);
          }

          setMessages((prev) => [
            ...prev,
            {
              id: wsMessage.messageId || `signup-${Date.now()}`,
              role: 'assistant',
              isSignupRequired: true,
              content: '',
            },
          ]);
          setCurrentTraceMessage('');
          setThinkingSteps([]);
          resetDirectiveState();
          setIsLoading(false);
          break;
        }

        case 'budget_exhausted': {
          if (enableDebugLogs) {
            console.log('💸 Budget exhausted received:', wsMessage);
          }
          const budgetData = wsMessage.data as
            | {
                message?: string;
                statusBadge?: { text?: string; variant?: string };
              }
            | undefined;
          // Add budget exhausted message with status badge
          setMessages((prev) => [
            ...prev,
            {
              id: wsMessage.messageId || Date.now().toString(),
              role: 'assistant',
              content:
                budgetData?.message ||
                "Buck is taking a quick break. We've hit our daily chat limit.",
              isBudgetExhausted: true,
              statusBadge: budgetData?.statusBadge || {
                text: 'Daily Limit Reached',
                variant: 'warning',
              },
              showDisclaimer: false,
              showHelpfulQuestion: false,
            },
          ]);
          // Disable input when budget is exhausted
          if (wsMessage.disableInput) {
            setIsInputDisabled(true);
          }
          setCurrentTraceMessage('');
          setThinkingSteps([]);
          resetDirectiveState();
          setIsLoading(false);
          break;
        }

        case 'lt_plus_cta': {
          if (enableDebugLogs) {
            console.log('🎯 LT+ CTA received:', wsMessage);
          }

          setMessages((prev) => [
            ...prev,
            {
              id: wsMessage.messageId || `signup-${Date.now()}`,
              role: 'assistant',
              ltPlusCta: wsMessage.data as ChatMessage['ltPlusCta'],
              content: '',
            },
          ]);
          setCurrentTraceMessage('');
          setThinkingSteps([]);
          resetDirectiveState();
          setIsLoading(false);
          break;
        }

        case 'package_cards': {
          if (enableDebugLogs) {
            console.log('📦 Package cards received (buffering until complete):', wsMessage);
          }
          // Buffer the cards - the assistant message doesn't exist yet (it's added on 'complete')
          packageCardsBufferRef.current = wsMessage.data as ChatMessage['packageCards'];
          break;
        }

        default:
          if (enableDebugLogs) console.log('Unknown message type:', wsMessage.type);
      }
    },
    [
      enableDebugLogs,
      sendToWebSocketInternal,
      isMessageCancelled,
      captureResponseDirective,
      resolveMessageDirective,
      resetDirectiveState,
    ]
  );

  // Initialize WebSocket connection.
  // If a connection is already in progress, callers await the same promise.
  const initializeWebSocket = useCallback((): Promise<void> => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (connectionPromiseRef.current) {
      if (enableDebugLogs) {
        console.log('WebSocket connection already in progress, reusing existing promise');
      }
      return connectionPromiseRef.current;
    }

    if (!buckChatUrl) {
      console.error('WebSocket URL not configured. Please provide websocketUrl in config.');
      return Promise.resolve();
    }

    // Build WebSocket URL with query parameters for context
    const urlParams = new URLSearchParams();
    if (listingId) urlParams.append('listingId', listingId);
    if (packageId) urlParams.append('packageId', packageId);
    if (pageType) urlParams.append('pageType', pageType);
    // Include conversationId for reconnection (maintains conversation context)
    if (conversationIdRef.current) urlParams.append('conversationId', conversationIdRef.current);
    const wsUrl = urlParams.toString() ? `${buckChatUrl}?${urlParams}` : buckChatUrl;

    if (enableDebugLogs) {
      console.log('Starting WebSocket connection to:', wsUrl);
    }

    // Request authentication token to be set as HttpOnly cookie
    // The cookie will be automatically sent with the WebSocket upgrade request
    connectionPromiseRef.current = requestAuthToken()
      .then((tokenRequested) => {
        // If token request failed, throw an error to prevent WebSocket connection attempt
        if (!tokenRequested) {
          const error = new Error('Failed to authenticate chat service') as Error & {
            name: string;
          };
          error.name = 'AUTH_TOKEN_FAILED';
          throw error;
        }

        return new Promise<void>((resolve, reject) => {
          // Connect to WebSocket - browser automatically sends cookies with upgrade request
          const ws = new WebSocket(wsUrl);
          wsRef.current = ws;

          if (enableDebugLogs) {
            console.log('WebSocket created, waiting for connection to open');
          }

          const timeout = setTimeout(() => {
            ws.close();
            reject(new Error('WebSocket connection timeout'));
          }, 10000);

          ws.onopen = () => {
            clearTimeout(timeout);
            if (enableDebugLogs) console.log('✅ WebSocket connected');
            setIsConnected(true);
            resolve();
          };

          ws.onmessage = (event) => {
            if (enableDebugLogs) console.log('📨 WebSocket message:', event.data);
            try {
              handleWebSocketMessage(JSON.parse(event.data) as WebSocketMessage);
            } catch (err) {
              console.error('Failed to parse WebSocket message:', err);
            }
          };

          ws.onclose = (event) => {
            clearTimeout(timeout);
            if (enableDebugLogs) console.log('❌ WebSocket disconnected', event.code, event.reason);
            setIsConnected(false);
            // Allow reconnection attempts
            connectionPromiseRef.current = null;

            if (ws.readyState !== WebSocket.OPEN) {
              reject(new Error(event.reason || `WebSocket closed (code: ${event.code})`));
            }
          };

          ws.onerror = () => {
            clearTimeout(timeout);
            setIsConnected(false);
            reject(new Error('WebSocket connection error'));
          };
        });
      })
      .catch((error) => {
        connectionPromiseRef.current = null;
        throw error;
      });

    return connectionPromiseRef.current;
  }, [
    enableDebugLogs,
    buckChatUrl,
    handleWebSocketMessage,
    requestAuthToken,
    listingId,
    packageId,
    pageType,
  ]);

  // Send message to WebSocket
  const sendMessage = useCallback(
    async (messageContent: string) => {
      if (!messageContent.trim() || isLoading) {
        return;
      }

      const trimmedMessage = messageContent.trim();

      // Reset retry count for new messages
      throttleRetryCountRef.current = 0;
      if (throttleRetryTimerRef.current) {
        clearTimeout(throttleRetryTimerRef.current);
      }

      // Add user message to chat immediately
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: trimmedMessage,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Initialize WebSocket on first message if not already connected
      try {
        setIsLoading(true);
        resetDirectiveState();
        setThinkingSteps([{ text: 'Understanding your question...', status: 'active' as const }]);
        setCurrentTraceMessage('');
        setQueueStatus(null);

        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          if (enableDebugLogs) {
            console.log('Send requested before WebSocket was open, waiting for connection');
          }
          await initializeWebSocket();
        }

        if (wsRef.current?.readyState !== WebSocket.OPEN) {
          throw new Error('WebSocket is not open after initialization');
        }

        sendToWebSocketInternal(trimmedMessage);
      } catch (error) {
        // Handle auth token failure and connection errors
        if (enableDebugLogs) console.error('WebSocket connection failed', error);
        setIsLoading(false);
        setCurrentTraceMessage('');
        setThinkingSteps([]);
        resetDirectiveState();
        setQueueStatus(null);

        const isAuthError = (error as Error & { name?: string }).name === 'AUTH_TOKEN_FAILED';
        const errorMessage = isAuthError
          ? 'Unable to authenticate chat service. Please refresh the page and try again.'
          : 'Unable to connect to the chat service. Please try again later.';

        // Add error message to chat
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            role: 'assistant',
            content: '',
            hasError: true,
            errorMessage,
            showDisclaimer: false,
            showHelpfulQuestion: false,
          },
        ]);
      }
    },
    [isLoading, initializeWebSocket, enableDebugLogs, sendToWebSocketInternal, resetDirectiveState]
  );

  // Retry the last message (manual retry)
  const retryLastMessage = useCallback(() => {
    // Find the last user message
    const lastUserMessage = [...messages].reverse().find((msg) => msg.role === 'user');

    if (!lastUserMessage) {
      if (enableDebugLogs) console.error('No user message to retry');
      return;
    }

    // Reset retry count
    throttleRetryCountRef.current = 0;

    // Remove the error/throttled message (last assistant message with error or throttle)
    setMessages((prev) => {
      const lastIndex = prev.length - 1;
      if (
        lastIndex >= 0 &&
        prev[lastIndex].role === 'assistant' &&
        (prev[lastIndex].hasError || prev[lastIndex].isThrottled)
      ) {
        return prev.slice(0, -1);
      }
      return prev;
    });

    // Resend the message
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      sendToWebSocketInternal(lastUserMessage.content);
    } else {
      if (enableDebugLogs) console.error('WebSocket is not connected for retry');
      // Try to reinitialize connection
      sendMessage(lastUserMessage.content);
    }
  }, [messages, sendMessage, enableDebugLogs, sendToWebSocketInternal]);

  // Cancel a queued request
  const cancelQueuedRequest = useCallback(() => {
    if (enableDebugLogs) {
      console.log('🚫 Cancelling queued request');
    }

    // Get the messageId to cancel
    const messageIdToCancel = queueStatus?.messageId || pendingMessageIdRef.current;

    // Send cancel message to backend if we have a messageId and connection is open
    if (messageIdToCancel && wsRef.current?.readyState === WebSocket.OPEN) {
      const cancelPayload = {
        type: 'cancel',
        messageId: messageIdToCancel,
        timestamp: new Date().toISOString(),
      };
      if (enableDebugLogs) {
        console.log('🚫 Sending cancel to backend:', cancelPayload);
      }
      wsRef.current.send(JSON.stringify(cancelPayload));
    }

    // Mark the pending message as cancelled so we ignore its responses (client-side backup)
    if (pendingMessageIdRef.current) {
      cancelledMessageIdsRef.current.add(pendingMessageIdRef.current);
      if (enableDebugLogs) {
        console.log('🚫 Marked message as cancelled:', pendingMessageIdRef.current);
      }
    }

    // Also mark the queueStatus messageId if different
    if (queueStatus?.messageId && queueStatus.messageId !== pendingMessageIdRef.current) {
      cancelledMessageIdsRef.current.add(queueStatus.messageId);
    }

    // Clear any pending retry timers
    if (throttleRetryTimerRef.current) {
      clearTimeout(throttleRetryTimerRef.current);
    }

    // Reset state
    throttleRetryCountRef.current = 0;
    pendingMessageIdRef.current = null;
    setQueueStatus(null);
    setCurrentTraceMessage('');
    setThinkingSteps([]);
    resetDirectiveState();
    setIsLoading(false);

    // Remove the last user message since we're cancelling
    setMessages((prev) => {
      const lastIndex = prev.length - 1;
      if (lastIndex >= 0 && prev[lastIndex].role === 'user') {
        return prev.slice(0, -1);
      }
      return prev;
    });

    // Cleanup: Remove old cancelled IDs to prevent memory leak (keep last 10)
    if (cancelledMessageIdsRef.current.size > 10) {
      const idsArray = Array.from(cancelledMessageIdsRef.current);
      cancelledMessageIdsRef.current = new Set(idsArray.slice(-10));
    }
  }, [enableDebugLogs, queueStatus, resetDirectiveState]);

  // Send feedback for a message (uses extracted feedbackApi utility)
  const sendFeedback = useCallback(
    async (messageId: string, isHelpful: boolean): Promise<void> => {
      if (!buckFeedbackUrl) {
        if (enableDebugLogs) {
          console.log('Feedback URL not configured. Skipping feedback submission.');
        }
        return;
      }

      const payload = {
        messageId,
        isHelpful,
        sessionId: conversationIdRef.current,
        timestamp: new Date().toISOString(),
        userId: currentUser?.id?.uuid,
        context: {
          listingId: listingId || undefined,
          packageId: packageId || undefined,
        },
      };

      try {
        await sendFeedbackApi(buckFeedbackUrl, payload, enableDebugLogs);
      } catch {
        // Fail silently - don't show error to user
      }
    },
    [buckFeedbackUrl, enableDebugLogs, currentUser, listingId, packageId]
  );

  const updateMessage = useCallback((messageId: string, updates: Partial<ChatMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ...updates } : m)));
  }, []);

  // Inject a mock WebSocket message for testing purposes
  const injectMockMessage = useCallback(
    (wsMessage: WebSocketMessage) => {
      if (enableDebugLogs) {
        console.log('🧪 Injecting mock WebSocket message:', wsMessage);
      }
      handleWebSocketMessage(wsMessage);
    },
    [enableDebugLogs, handleWebSocketMessage]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        if (enableDebugLogs) console.log('Cleaning up WebSocket connection');
        wsRef.current.close();
      }
      if (throttleRetryTimerRef.current) {
        clearTimeout(throttleRetryTimerRef.current);
      }
    };
  }, [enableDebugLogs]);

  return {
    messages,
    initializeWebSocket,
    sendMessage,
    retryLastMessage,
    cancelQueuedRequest,
    sendFeedback,
    updateMessage,
    injectMockMessage, // For testing: inject mock WebSocket messages
    isConnected,
    isLoading,
    isInputDisabled, // True when budget is exhausted
    currentTraceMessage, // User-facing trace message for dynamic "thinking" text
    thinkingSteps, // Accumulated thinking steps for ThinkingSteps component
    activeResponseDirective, // Orchestration directive for the in-flight turn
    queueStatus, // { position, estimatedWait, message } when queued
  };
};

export default usePackageChat;
